import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API, money, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Archive } from "lucide-react";

const empty = {
  sku: "", barcode: "", name: "", brand: "", description: "", category: "", supplier: "",
  purchase_price: "", selling_price: "", tender_price: "", vat: 15, markup_pct: "",
  stock_quantity: 0, reorder_level: 10, image_url: "", status: "Active",
};

const MARKUPS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
// Spreadsheet: VAT = Cost×VAT% · Cost+VAT · Selling = (Cost+VAT) × (1 + markup%)
const mk = (f) => { const cost = Number(f.purchase_price || 0), vat = cost * Number(f.vat || 0) / 100, incl = cost + vat; const sell = f.markup_pct === "" ? Number(f.selling_price || 0) : incl * (1 + Number(f.markup_pct || 0) / 100); return { vat, incl, sell }; };
const r2 = (n) => Math.round(Number(n) * 100) / 100;
const applyMarkup = (f) => (f.markup_pct === "" ? f : { ...f, selling_price: String(r2(mk(f).sell)) });
const Calc = ({ l, v, hl, testId }) => <div data-testid={testId}><div className="text-[10px] uppercase text-muted-foreground">{l}</div><div className={`font-semibold tabular ${hl ? "text-primary text-sm" : ""}`}>{v}</div></div>;

export default function Products() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const editable = can("products");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const [sp, setSp] = useSearchParams();
  const section = sp.get("section") === "gas" ? "gas" : "ppe";
  const isGas = section === "gas";
  const setSection = (s) => { setSp(s === "gas" ? { section: "gas" } : {}); setCat("all"); };
  const GAS_CAT = "Gas & Cylinders";
  const visibleCats = cats.filter((c) => (isGas ? c === GAS_CAT : c !== GAS_CAT));

  const load = async () => {
    const params = { include_inactive: true, gas: isGas };
    if (search) params.search = search;
    if (cat !== "all") params.category = cat;
    const { data } = await api.get("/products", { params });
    setItems(data);
  };

  useEffect(() => {
    api.get("/meta/categories").then(({ data }) => setCats(data.categories));
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
  }, []);
  useEffect(() => { load(); }, [search, cat, isGas]);

  const openNew = () => { setForm({ ...empty, category: isGas ? GAS_CAT : "" }); setEditing(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); setOpen(true); };

  const save = async () => {
    const payload = { ...form };
    delete payload.cost_incl;
    ["purchase_price", "selling_price", "tender_price", "vat", "stock_quantity", "reorder_level"].forEach((k) => {
      payload[k] = Number(payload[k]) || 0;
    });
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const archive = async (p) => {
    if (!(await confirm({ title: "Archive product?", description: `"${p.name}" will be marked inactive and hidden from catalogues. You can reactivate it later by editing it.`, confirmText: "Archive" }))) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Product archived");
    load();
  };

  return (
    <div>
      <PageHeader title="Product Catalogue" subtitle={`${items.length} products across ${cats.length} categories`}>
        {editable && (
          <Button onClick={openNew} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="add-product-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add Product
          </Button>
        )}
      </PageHeader>

      <Tabs value={section} onValueChange={setSection} className="mb-4">
        <TabsList data-testid="product-section-tabs">
          <TabsTrigger value="ppe" data-testid="section-tab-ppe">PPE &amp; General</TabsTrigger>
          <TabsTrigger value="gas" data-testid="section-tab-gas">Gas POS</TabsTrigger>
        </TabsList>
      </Tabs>
      {isGas && <p className="text-xs text-muted-foreground mb-3" data-testid="gas-section-note">Gas Exchange catalogue — these items and prices only appear on the Gas POS, never on the PPE side.</p>}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, SKU, barcode..." className="pl-9 h-10 bg-white" data-testid="product-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[240px] h-10 bg-white" data-testid="category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {visibleCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary hover:bg-secondary">
              <TableHead>SKU Code</TableHead>
              <TableHead>Barcode</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Brand Description</TableHead>
              <TableHead className="text-right">Cost (Excl / Incl VAT)</TableHead>
              <TableHead className="text-right">Selling Price</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
                <TableCell className="font-mono text-xs text-muted-foreground">{p.sku}</TableCell>
                <TableCell>{p.barcode ? <a href={`${API}/products/${p.id}/barcode.svg`} target="_blank" rel="noreferrer" title="Open printable barcode" data-testid={`barcode-${p.sku}`}><img src={`${API}/products/${p.id}/barcode.svg`} alt={p.barcode} className="h-9 w-28 object-contain" /><div className="font-mono text-[10px] text-muted-foreground">{p.barcode}</div></a> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.category}</TableCell>
                <TableCell><div className="font-medium">{p.brand ? `${p.brand} — ` : ""}{p.name}</div>{p.description && <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>}</TableCell>
                <TableCell className="text-right tabular">{money(p.purchase_price)}<div className="text-[10px] text-muted-foreground">incl VAT {money(Number(p.purchase_price || 0) * (1 + Number(p.vat || 0) / 100))}</div></TableCell>
                <TableCell className="text-right tabular font-medium">{money(p.selling_price)}{p.markup_pct != null && p.markup_pct !== "" && <div className="text-[10px] text-muted-foreground">+{p.markup_pct}% m/up</div>}</TableCell>
                <TableCell className="text-right tabular">
                  <span className={Number(p.stock_quantity) <= Number(p.reorder_level) ? "text-primary font-bold" : ""}>{p.stock_quantity}</span>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.status === "Active" ? "border-green-600 text-green-700" : "border-zinc-300 text-zinc-500"}>{p.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {editable && (
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-product-${p.sku}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => archive(p)} data-testid={`archive-product-${p.sku}`}><Archive className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No products found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Product Name" className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></Field>
            <Field label="SKU / Code"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku-input" /></Field>
            <Field label="Barcode (EAN-13, auto-generated)"><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Leave blank to auto-generate" data-testid="product-barcode-input" /></Field>
            <Field label="Brand"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Dromex" data-testid="product-brand-input" /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={isGas}>
                <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{visibleCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Supplier">
              <Select value={form.supplier} onValueChange={(v) => setForm({ ...form, supplier: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Cost (Excl VAT)"><Input type="number" value={form.purchase_price} onChange={(e) => setForm(applyMarkup({ ...form, purchase_price: e.target.value, cost_incl: "" }))} placeholder="e.g. 1000" data-testid="product-cost-input" /></Field>
            <Field label={`Cost (Incl VAT ${Number(form.vat || 0)}%)`}><Input type="number" value={form.cost_incl || (form.purchase_price === "" ? "" : r2(mk(form).incl))} onChange={(e) => { const v = e.target.value; const excl = v === "" ? "" : String(r2(Number(v) / (1 + Number(form.vat || 0) / 100))); setForm(applyMarkup({ ...form, purchase_price: excl, cost_incl: v })); }} placeholder="e.g. 1150" data-testid="product-cost-incl-input" /></Field>
            <Field label="Mark-up %">
              <div className="flex gap-1">
                <select value={form.markup_pct} onChange={(e) => setForm(applyMarkup({ ...form, markup_pct: e.target.value }))} className="h-10 flex-1 rounded-md border border-input bg-white px-2 text-sm" data-testid="product-markup-select"><option value="">— manual price —</option>{MARKUPS.map((m) => <option key={m} value={m}>{m}%</option>)}</select>
                <Input type="number" value={form.markup_pct} onChange={(e) => setForm(applyMarkup({ ...form, markup_pct: e.target.value }))} placeholder="custom" className="w-24" data-testid="product-markup-input" />
              </div>
            </Field>
            <div className="col-span-2 rounded-md bg-secondary/70 p-3 text-xs grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="product-markup-calc">
              <Calc l="Cost (Excl VAT)" v={money(Number(form.purchase_price || 0))} testId="calc-cost-excl" /><Calc l={`VAT ${Number(form.vat || 0)}% on cost`} v={money(mk(form).vat)} /><Calc l="Cost (Incl VAT)" v={money(mk(form).incl)} testId="calc-cost-incl" /><Calc l={`Mark-up ${Number(form.markup_pct || 0)}%`} v={money(mk(form).sell - mk(form).incl)} /><Calc l="Selling Price" v={money(mk(form).sell)} hl />
            </div>
            <div className="col-span-2" data-testid="markup-comparison">
              <div className="text-[10px] uppercase text-muted-foreground mb-1">Mark-up comparison (selling price from Cost Incl VAT) — click to apply</div>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
                {[5, 10, 15, 20, 25, 30, 40, 50].map((m) => (
                  <button key={m} type="button" onClick={() => setForm(applyMarkup({ ...form, markup_pct: String(m) }))} data-testid={`markup-compare-${m}`} className={`rounded-sm border px-1 py-1.5 text-center transition-colors ${String(form.markup_pct) === String(m) ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary"}`}>
                    <div className="text-[10px] font-semibold">{m}%</div>
                    <div className="text-[11px] tabular">{money(r2(mk(form).incl * (1 + m / 100)))}</div>
                  </button>
                ))}
              </div>
            </div>
            <Field label="Selling Price (auto — editable)"><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value, markup_pct: "" })} data-testid="product-price-input" /></Field>
            <Field label="VAT %"><Input type="number" value={form.vat} onChange={(e) => setForm(applyMarkup({ ...form, vat: e.target.value, cost_incl: "" }))} data-testid="product-vat-input" /></Field>
            <Field label="Stock Quantity"><Input type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} /></Field>
            <Field label="Reorder Level"><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></Field>
            <Field label="Image URL" className="col-span-2"><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://" /></Field>
            <Field label="Description" className="col-span-2"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-product-button">Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
